import {
	IPostMediaFile,
	IGetRestaurantInfo,
	IGetRestaurantsShortArgs,
	IGetRestaurantsShort,
	ICreateRestaurant,
	IUpdateRestaurant,
	IGetPaginatedRestaurants,
	IRestaurants,
	IGetRestaurantLocalizationInfoArgs,
	IGetRestaurantLocalizationInfo,
	ICreateRestaurantLocalization,
	IUpdateRestaurantLocalization,
	IGetRestaurantStylesInfo,
	IUpdateRestaurantStyles,
	IGetRestaurantDishes,
	ICreateRestaurantWatchesCommandsFile,
	IPushNotification,
	IGetLatestSoftwareUpdateInfo,
	IGetPaginatedRestaurantSoftwareUpdates,
	ISoftwareUpdates,
	IGetPaginatedRestaurantDevices,
	IRestaurantDevices,
	IAssignDevices,
	IRestaurantsCopyMenu,
	IGetReportsInfo,
	IGetRestaurantReportsInfo,
	ICreateRestaurantReports,
	IUpdateRestaurantReports,
	IGetRestaurantBankSettings,
	IUpdateRestaurantBankSettings,
	IGetRestaurantTlsCertificateInfo,
	IGetRestaurantNfcCards,
	IUpdateRestaurantNfcCards,
} from 'types';
import { AxiosResponse } from 'axios';
import { request } from '../index';

const serviceGetRestaurantInfo = (restaurantID: string): Promise<IGetRestaurantInfo> => {
	return request({ typeOfRequest: 'getWithToken', url: `/api/Restaurants/${restaurantID}` });
};

const serviceGetRestaurantsShort = (args: IGetRestaurantsShortArgs): Promise<IGetRestaurantsShort> => {
	return request({
		typeOfRequest: 'getWithToken',
		url: `/api/Restaurants/short?customerId=${args.customerId}`,
		config: {
			headers: {
				'X-Menu-Api-Ver': args.menuApiVersion,
			},
		},
	});
};

const serviceGetRestaurantsByCategoryShort = (categoryId: string): Promise<IGetRestaurantsShort> => {
	return request({ typeOfRequest: 'getWithToken', url: `/api/v2/categories/${categoryId}/restaurants` });
};

const serviceGetRestaurantsByDishShort = (dishId: string): Promise<IGetRestaurantsShort> => {
	return request({ typeOfRequest: 'getWithToken', url: `/api/v2/dishes/${dishId}/restaurants` });
};

const serviceCreateRestaurant = (data: ICreateRestaurant): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'postWithToken', url: `/api/Restaurants`, data });
};

const serviceUpdateRestaurant = (data: IUpdateRestaurant): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'putWithToken', url: `/api/Restaurants/${data.restaurantId}`, data });
};

const serviceGetPaginatedRestaurants = (args: IGetPaginatedRestaurants): Promise<AxiosResponse<IRestaurants>> => {
	return request({ typeOfRequest: 'postWithToken', url: `/api/Restaurants/filtered`, data: args });
};

const serviceDeleteRestaurant = (restaurantID: string): Promise<AxiosResponse<void>> => {
	return request({
		typeOfRequest: 'deleteWithTokenAndWithParameterInUrlLine',
		url: `/api/Restaurants/${restaurantID}`,
	});
};

const serviceArchiveRestaurant = (restaurantID: string): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'postWithToken', url: `/api/Restaurants/${restaurantID}/archive` });
};

const serviceRestoreRestaurant = (restaurantID: string): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'postWithToken', url: `/api/Restaurants/${restaurantID}/restore` });
};

const serviceGetRestaurantLocalizationInfo = (
	args: IGetRestaurantLocalizationInfoArgs,
): Promise<IGetRestaurantLocalizationInfo> => {
	return request({
		typeOfRequest: 'getWithToken',
		url: `/api/RestaurantLocalizations/${args.customerID}/${args.restaurantID}${
			args.useCustomerSettings !== undefined ? '?useCustomerSettings=' + args.useCustomerSettings : ''
		}`,
	});
};

const serviceCreateRestaurantLocalization = (data: ICreateRestaurantLocalization): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'postWithToken', url: `/api/RestaurantLocalizations`, data });
};

const serviceUpdateRestaurantLocalization = (data: IUpdateRestaurantLocalization): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'putWithToken', url: `/api/RestaurantLocalizations`, data });
};

const serviceGetRestaurantStylesInfo = (
	customerID: string,
	restaurantID: string,
): Promise<IGetRestaurantStylesInfo> => {
	return request({ typeOfRequest: 'getWithToken', url: `/api/RestaurantStyles/${customerID}/${restaurantID}` });
};

const serviceUpdateRestaurantStylesInfo = (
	customerID: string,
	restaurantID: string,
	data: IUpdateRestaurantStyles,
): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'putWithToken', url: `/api/RestaurantStyles/${customerID}/${restaurantID}`, data });
};

const serviceGetRestaurantDishes = (restaurantID: string): Promise<IGetRestaurantDishes> => {
	return request({ typeOfRequest: 'getWithToken', url: `/api/Restaurants/${restaurantID}/dishes` });
};

const serviceCreateRestaurantWatchesCommandsFile = (
	restaurantID: string,
	data: ICreateRestaurantWatchesCommandsFile,
): Promise<AxiosResponse<void>> => {
	return request({
		typeOfRequest: 'postWithToken',
		url: `/api/Restaurants/${restaurantID}/watches-commands-file`,
		data,
	});
};

const servicePushRestaurantNotification = (
	restaurantID: string,
	data: IPushNotification,
): Promise<AxiosResponse<void>> => {
	return request({
		typeOfRequest: 'postWithToken',
		url: `/api/v1/restaurants/${restaurantID}/push-notifications:register`,
		data,
	});
};

const serviceGetLatestRestaurantSoftwareUpdatesData = (restaurantID: string): Promise<IGetLatestSoftwareUpdateInfo> => {
	return request({ typeOfRequest: 'getWithToken', url: `/api/restaurants/${restaurantID}/software/audits/latest` });
};

const serviceGetPaginatedRestaurantSoftwareUpdates = (
	args: IGetPaginatedRestaurantSoftwareUpdates,
): Promise<AxiosResponse<ISoftwareUpdates>> => {
	return request({ typeOfRequest: 'postWithToken', url: `/api/restaurants/software/audits/filtered`, data: args });
};

const serviceGetPaginatedRestaurantDevices = (
	args: IGetPaginatedRestaurantDevices,
): Promise<AxiosResponse<IRestaurantDevices>> => {
	const restaurantID = args.restaurantID;
	const copyArgs = { ...args };
	delete copyArgs.restaurantID;
	return request({
		typeOfRequest: 'postWithToken',
		url: `/api/restaurants/${restaurantID}/devices/filtered`,
		data: copyArgs,
	});
};

const serviceAssignDevices = (data: IAssignDevices): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'postWithToken', url: `/api/restaurants/${data.restaurantId}/devices`, data });
};

// Menu v1
const serviceCopyMenu = (data: IRestaurantsCopyMenu): Promise<AxiosResponse<void>> => {
	return request({
		typeOfRequest: 'postWithToken',
		url: `/api/restaurants/${data.fromRestaurantId}/copy-menu`,
		data,
	});
};
// Menu v1
// Menu v2
const serviceCopyMenuV2 = (data: IRestaurantsCopyMenu): Promise<AxiosResponse<void>> => {
	return request({
		typeOfRequest: 'postWithToken',
		url: `/api/v2/restaurants/${data.fromRestaurantId}/copy-menu`,
		data,
	});
};
// Menu v2

const serviceGetReportsInfo = (reportsID: string): Promise<IGetReportsInfo> => {
	return request({ typeOfRequest: 'getWithToken', url: `/api/Reports/${reportsID}` });
};

const serviceGetRestaurantReportsInfo = (restaurantReportsID: string): Promise<IGetRestaurantReportsInfo> => {
	return request({ typeOfRequest: 'getWithToken', url: `/api/Reports?restaurantId=${restaurantReportsID}` });
};

const serviceCreateRestaurantReports = (data: ICreateRestaurantReports): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'postWithToken', url: '/api/Reports', data });
};

const serviceUpdateRestaurantReports = (data: IUpdateRestaurantReports): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'putWithToken', url: `/api/Reports/${data.id}`, data });
};

const serviceDeleteRestaurantReports = (reportID: string): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'deleteWithTokenAndWithParameterInUrlLine', url: `/api/Reports/${reportID}` });
};

const serviceUploadRestaurantBankSettings = (data: FormData): Promise<IPostMediaFile> => {
	return request({ typeOfRequest: 'postWithToken', url: `/api/Restaurants/bank-setting-file`, data });
};

const serviceGetRestaurantBankSettings = (restaurantID: string): Promise<IGetRestaurantBankSettings> => {
	return request({ typeOfRequest: 'getWithToken', url: `/api/RestaurantBankSettings/${restaurantID}` });
};

const serviceUpdateRestaurantBankSettings = (data: IUpdateRestaurantBankSettings): Promise<AxiosResponse<void>> => {
	return request({ typeOfRequest: 'putWithToken', url: `/api/RestaurantBankSettings/${data.restaurantId}`, data });
};

const serviceGetRestaurantTlsCertificateInfo = (restaurantID: string): Promise<IGetRestaurantTlsCertificateInfo> => {
	return request({ typeOfRequest: 'getWithToken', url: `/api/Restaurants/${restaurantID}/tls-certificate-info` });
};

const serviceGetRestaurantNfcCard = (restaurantID: string): Promise<IGetRestaurantNfcCards> => {
	return request({ typeOfRequest: 'getWithToken', url: `/api/v1/NfcCards/restaurant/${restaurantID}` });
};

const serviceUpdateRestaurantNfcCard = (data: IUpdateRestaurantNfcCards): Promise<AxiosResponse<void>> => {
	return request({
		typeOfRequest: 'putWithToken',
		url: `/api/v1/NfcCards/restaurant/${data.restaurantId}`,
		data: data.restaurantNfcCards,
	});
};

export {
	serviceGetRestaurantInfo,
	serviceGetRestaurantsShort,
	serviceGetRestaurantsByCategoryShort,
	serviceGetRestaurantsByDishShort,
	serviceCreateRestaurant,
	serviceUpdateRestaurant,
	serviceGetPaginatedRestaurants,
	serviceDeleteRestaurant,
	serviceArchiveRestaurant,
	serviceRestoreRestaurant,
	serviceGetRestaurantLocalizationInfo,
	serviceCreateRestaurantLocalization,
	serviceUpdateRestaurantLocalization,
	serviceGetRestaurantStylesInfo,
	serviceUpdateRestaurantStylesInfo,
	serviceGetRestaurantDishes,
	serviceCreateRestaurantWatchesCommandsFile,
	servicePushRestaurantNotification,
	serviceGetLatestRestaurantSoftwareUpdatesData,
	serviceGetPaginatedRestaurantSoftwareUpdates,
	serviceGetPaginatedRestaurantDevices,
	serviceAssignDevices,
	serviceCopyMenu,
	serviceCopyMenuV2,
	serviceGetReportsInfo,
	serviceGetRestaurantReportsInfo,
	serviceCreateRestaurantReports,
	serviceUpdateRestaurantReports,
	serviceDeleteRestaurantReports,
	serviceUploadRestaurantBankSettings,
	serviceGetRestaurantBankSettings,
	serviceUpdateRestaurantBankSettings,
	serviceGetRestaurantTlsCertificateInfo,
	serviceGetRestaurantNfcCard,
	serviceUpdateRestaurantNfcCard,
};
